import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronDown, ChevronRight, ChevronUp, FileText, Search, Send } from 'lucide-react';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MenuItem } from '@/components/ui/Popover';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { StatusChip, type StatusTone } from '@/components/ui/Chip';
import { ConfirmActionModal, PatientTabMoreMenu } from '@/components/patient/PatientTabActions';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';
import { FormsResponsePanel, type ResponseHistoryPoint } from '@/components/patient/FormsResponsePanel';
import { SendFormsAssignmentModal } from './SendFormsAssignmentModal';
import { QuestionnaireTagChips, QuestionnaireTagFilter } from '@/components/questionnaires/QuestionnaireTagFilter';
import {
  useCancelQuestionnaire,
  useFormsTenantStatus,
  usePatientFormsAssignments,
  useResendQuestionnaire,
  type FormsAssignmentStatus,
  type PatientFormsAssignment,
} from '@/hooks/useClinioForms';
import { useFormsAssignmentDetails } from '@/hooks/useFormsAssignmentResponses';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import { useQuestionnaireNameMap } from '@/hooks/useQuestionnaireNameMap';
import {
  buildQuestionnaireHistoryRow,
  type QuestionnaireHistoryRow,
} from '@/lib/questionnaireAssignmentHistory';
import { formatClinicDateOnly, formatClinicDateTime } from '@/lib/questionnaireDateFormat';
import {
  assignmentStoredTotalIsZero,
  historyScoreAfterShownTotal,
  listQuestionnaireScore,
  scoreNumericValue,
  sparklinePoints,
  type ScoreDisplay,
} from '@/lib/questionnaireScoreDisplay';
import { getSeverityLabel } from '@/lib/severityUtils';
import { collectQuestionnaireTags, matchesQuestionnaireTags } from '@/lib/questionnaireTags';
import { cn } from '@/utils/cn';

const REMINDER_MAX = 2;

type StatusFilter = 'all' | 'awaiting' | 'completed' | 'closed';

interface FormsAssignmentTabProps {
  patientId: string;
  clinicId: string;
}

function formsStatusTone(status: FormsAssignmentStatus): StatusTone {
  switch (status) {
    case 'sent':
      return 'upcoming';
    case 'reminded':
      return 'draft';
    case 'completed':
      return 'completed';
    case 'expired':
      return 'neutral';
    case 'cancelled':
      return 'cancelled';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function isAwaiting(status: FormsAssignmentStatus): boolean {
  return status === 'sent' || status === 'reminded';
}

function isClosed(status: FormsAssignmentStatus): boolean {
  return status === 'expired' || status === 'cancelled';
}

function tagsForTemplate(tagsById: Map<string, string[]>, templateId: string): string[] {
  return tagsById.get(templateId) ?? [];
}

function scoreLine(
  score: ScoreDisplay,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  switch (score.kind) {
    case 'none':
      return null;
    case 'total':
      return score.max != null ? `${score.value}/${score.max}` : String(score.value);
    case 'physical_mental':
      return `${t('forms.score_physical', { value: score.physical })} · ${t('forms.score_mental', { value: score.mental })}`;
    default: {
      const _exhaustive: never = score;
      return _exhaustive;
    }
  }
}

export function FormsAssignmentTab({ patientId, clinicId }: FormsAssignmentTabProps) {
  const { t, i18n } = useTranslation();
  const { data: assignments, isLoading } = usePatientFormsAssignments(patientId, clinicId);
  const tenantStatus = useFormsTenantStatus(clinicId);
  const templatesQuery = useFormsTemplateList(clinicId, i18n.language);
  const templateIds = useMemo(
    () => (assignments ?? []).map((row) => row.forms_template_id),
    [assignments],
  );
  const { map: templateNames } = useQuestionnaireNameMap(clinicId, i18n.language, templateIds);
  const unnamed = t('forms.unnamed_questionnaire', 'Spørgeskema');
  const resendMutation = useResendQuestionnaire();
  const cancelMutation = useCancelQuestionnaire();

  const tagsById = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of [...(templatesQuery.data?.platform ?? []), ...(templatesQuery.data?.clinic ?? [])]) {
      map.set(row.id, row.tags ?? []);
      if (row.questionnaire_id && row.questionnaire_id !== row.id) {
        map.set(row.questionnaire_id, row.tags ?? []);
      }
    }
    return map;
  }, [templatesQuery.data]);

  const historyById = useMemo(() => {
    const map = new Map<string, QuestionnaireHistoryRow>();
    for (const assignment of assignments ?? []) {
      map.set(
        assignment.id,
        buildQuestionnaireHistoryRow(assignment, templateNames, i18n.language, unnamed),
      );
    }
    return map;
  }, [assignments, templateNames, i18n.language, unnamed]);

  const zeroScoreIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of assignments ?? []) {
      if (row.status !== 'completed') continue;
      const name = historyById.get(row.id)?.questionnaireName ?? unnamed;
      if (assignmentStoredTotalIsZero(row.score, name)) ids.push(row.forms_assignment_id);
    }
    return ids;
  }, [assignments, historyById, unnamed]);
  const scoreDetailQueries = useFormsAssignmentDetails(clinicId, zeroScoreIds);
  const detailByFormsId = new Map<string, (typeof scoreDetailQueries)[number]>();
  zeroScoreIds.forEach((id, index) => {
    const queryResult = scoreDetailQueries[index];
    if (queryResult) detailByFormsId.set(id, queryResult);
  });

  const [query, setQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [closedOpen, setClosedOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [responseAssignment, setResponseAssignment] = useState<PatientFormsAssignment | null>(null);
  const [cancelAssignment, setCancelAssignment] = useState<PatientFormsAssignment | null>(null);

  const availableTags = useMemo(() => {
    const rows = (assignments ?? []).map((row) => ({
      tags: tagsForTemplate(tagsById, row.forms_template_id),
    }));
    return collectQuestionnaireTags(rows);
  }, [assignments, tagsById]);

  const searchMatched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (assignments ?? []).filter((assignment) => {
      const history = historyById.get(assignment.id);
      const tags = tagsForTemplate(tagsById, assignment.forms_template_id);
      if (!matchesQuestionnaireTags(tags, selectedTags)) return false;
      if (!needle) return true;
      const hay = [history?.questionnaireName, history?.serviceName, tags.join(' ')]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [assignments, historyById, query, selectedTags, tagsById]);

  const filtered = useMemo(() => {
    return searchMatched.filter((assignment) => {
      if (statusFilter === 'awaiting' && !isAwaiting(assignment.status)) return false;
      if (statusFilter === 'completed' && assignment.status !== 'completed') return false;
      if (statusFilter === 'closed' && !isClosed(assignment.status)) return false;
      return true;
    });
  }, [searchMatched, statusFilter]);

  const awaiting = useMemo(
    () =>
      filtered
        .filter((row) => isAwaiting(row.status))
        .slice()
        .sort((a, b) => (b.sent_at ?? '').localeCompare(a.sent_at ?? '')),
    [filtered],
  );

  const completedGroups = useMemo(() => {
    const byTemplate = new Map<string, PatientFormsAssignment[]>();
    for (const row of filtered.filter((item) => item.status === 'completed')) {
      const list = byTemplate.get(row.forms_template_id) ?? [];
      list.push(row);
      byTemplate.set(row.forms_template_id, list);
    }
    return Array.from(byTemplate.entries())
      .map(([templateId, rows]) => {
        const ordered = rows
          .slice()
          .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''));
        return { templateId, latest: ordered[0]!, all: ordered };
      })
      .sort((a, b) => (b.latest.completed_at ?? '').localeCompare(a.latest.completed_at ?? ''));
  }, [filtered]);

  const closed = useMemo(
    () =>
      filtered
        .filter((row) => isClosed(row.status))
        .slice()
        .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    [filtered],
  );

  const statusCounts = useMemo(() => {
    const awaitingCount = searchMatched.filter((row) => isAwaiting(row.status)).length;
    const completedCount = searchMatched.filter((row) => row.status === 'completed').length;
    const closedCount = searchMatched.filter((row) => isClosed(row.status)).length;
    return {
      all: searchMatched.length,
      awaiting: awaitingCount,
      completed: completedCount,
      closed: closedCount,
    };
  }, [searchMatched]);

  const filtersActive = Boolean(query.trim() || selectedTags.length || statusFilter !== 'all');
  const empty = !isLoading && (!assignments || assignments.length === 0);
  const tenantKnown = tenantStatus.isSuccess || tenantStatus.isError;
  const tenantReady = tenantStatus.data?.status === 'active';
  const showClosed = statusFilter !== 'completed' && statusFilter !== 'awaiting' && closed.length > 0;
  const closedExpanded = closedOpen || statusFilter === 'closed';

  function reminderLabel(assignment: PatientFormsAssignment): string {
    const count = assignment.reminder_count ?? 0;
    if (assignment.status === 'reminded' || count > 0) {
      return t('forms.status_reminded_count', {
        count,
        max: REMINDER_MAX,
        defaultValue: `Påmindet ${count} af ${REMINDER_MAX}`,
      });
    }
    return t('forms.status.sent');
  }

  function statusChip(assignment: PatientFormsAssignment) {
    if (isAwaiting(assignment.status)) {
      return <StatusChip tone={formsStatusTone(assignment.status)}>{reminderLabel(assignment)}</StatusChip>;
    }
    return (
      <StatusChip tone={formsStatusTone(assignment.status)}>
        {t(`forms.status.${assignment.status}`)}
      </StatusChip>
    );
  }

  function scoreStateFor(row: PatientFormsAssignment, templateName: string) {
    const detailQuery = detailByFormsId.get(row.forms_assignment_id);
    return listQuestionnaireScore({
      storedScore: row.score,
      templateName,
      detail: detailQuery?.data ?? null,
      detailPending: Boolean(detailQuery && !detailQuery.data && !detailQuery.isError),
    });
  }

  function responseHistory(templateId: string): ResponseHistoryPoint[] {
    return (assignments ?? [])
      .filter((row) => row.forms_template_id === templateId && row.status === 'completed')
      .map((row) => {
        const templateName = historyById.get(row.id)?.questionnaireName ?? unnamed;
        const state = scoreStateFor(row, templateName);
        return {
          formsAssignmentId: row.forms_assignment_id,
          completedAt: row.completed_at,
          score: historyScoreAfterShownTotal(row.score, state, templateName),
        };
      });
  }

  function appointmentCell(history: QuestionnaireHistoryRow) {
    if (!history.appointmentStartsAt) {
      return <div className="c mu">{t('forms.no_appointment', 'Uden aftale')}</div>;
    }
    return (
      <div className="c">
        <span className="ell">{formatClinicDateTime(history.appointmentStartsAt, i18n.language)}</span>
        {history.serviceName ? <small>{history.serviceName}</small> : null}
      </div>
    );
  }

  function clearFilters() {
    setQuery('');
    setSelectedTags([]);
    setStatusFilter('all');
  }

  return (
    <>
      <PatientTabFrame
        title={t('patients.tabs.questionnaires')}
        count={isLoading ? undefined : assignments?.length ?? 0}
        action={
          <Button type="button" onClick={() => setShowSendModal(true)}>
            <Send />
            {t('forms.send_questionnaire')}
          </Button>
        }
        loading={isLoading}
        banner={
          tenantKnown && !tenantReady ? (
            <Banner tone="warning" title={t('forms.modal.tenant_not_active')} />
          ) : undefined
        }
        empty={
          empty
            ? {
                icon: <FileText className="h-7 w-7" />,
                title: t('forms.no_assignments_title'),
                description: t('forms.no_assignments_desc'),
              }
            : undefined
        }
      >
        <div className="sk">
          <div className="sk-tools">
            <div className="search">
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('forms.search_placeholder', 'Søg i skemaer')}
                aria-label={t('forms.search_placeholder', 'Søg i skemaer')}
                leadingIcon={<Search className="i sm" aria-hidden />}
              />
            </div>
            <SegmentedControl
              ariaLabel={t('forms.detail.status', 'Status')}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'all', label: t('forms.filter_all', 'Alle'), count: statusCounts.all },
                { value: 'awaiting', label: t('forms.filter_awaiting', 'Afventer svar'), count: statusCounts.awaiting },
                { value: 'completed', label: t('forms.filter_completed', 'Udfyldt'), count: statusCounts.completed },
                { value: 'closed', label: t('forms.filter_closed', 'Udløbet/annulleret'), count: statusCounts.closed },
              ]}
            />
          </div>
          <QuestionnaireTagFilter
            tags={availableTags}
            selected={selectedTags}
            onChange={setSelectedTags}
            labelled
            usageSource={(assignments ?? []).map((row) => ({
              tags: tagsForTemplate(tagsById, row.forms_template_id),
            }))}
          />
          {filtersActive ? (
            <div className="sk-meta">
              <span>
                {t('forms.showing_filtered', {
                  shown: filtered.length,
                  total: assignments?.length ?? 0,
                  defaultValue: `Viser ${filtered.length} af ${assignments?.length ?? 0}`,
                })}
              </span>
              <button type="button" className="sk-clear" onClick={clearFilters}>
                {t('forms.clear_filters', 'Ryd filtre')}
              </button>
            </div>
          ) : null}

          {awaiting.length > 0 ? (
            <section className="sk-sec" aria-labelledby="s-wait">
              <h3 id="s-wait">
                {t('forms.section_awaiting', 'Afventer svar')}
                <span className="count num">{awaiting.length}</span>
              </h3>
              <div className="sk-cols sk-wait" aria-hidden="true">
                <span>{t('forms.col_questionnaire', 'Spørgeskema')}</span>
                <span>{t('forms.col_appointment', 'Aftale')}</span>
                <span>{t('forms.col_sent', 'Sendt')}</span>
                <span>{t('forms.col_due', 'Frist')}</span>
                <span>{t('forms.col_status', 'Status')}</span>
                <span />
              </div>
              <ul className="sk-l">
                {awaiting.map((assignment) => {
                  const history = historyById.get(assignment.id);
                  if (!history) return null;
                  const tags = tagsForTemplate(tagsById, assignment.forms_template_id);
                  const canRemind = (assignment.reminder_count ?? 0) < REMINDER_MAX;
                  return (
                    <li key={assignment.id} className="sk-it">
                      <div className="sk-row sk-wait">
                        <div className="nm">
                          <b>{history.questionnaireName}</b>
                          <QuestionnaireTagChips
                            tags={tags}
                            emptyLabel={t('forms.no_tags', 'Ingen tags')}
                          />
                        </div>
                        {appointmentCell(history)}
                        <div className="c num">{formatClinicDateOnly(assignment.sent_at, i18n.language) || '—'}</div>
                        <div className="c num">{formatClinicDateOnly(assignment.due_at, i18n.language) || '—'}</div>
                        <div>{statusChip(assignment)}</div>
                        <div className="acts">
                          {canRemind ? (
                            <button
                              type="button"
                              className="sk-tb"
                              disabled={resendMutation.isPending}
                              onClick={() =>
                                resendMutation.mutate({ assignment_id: assignment.id, patient_id: patientId })
                              }
                            >
                              <Bell className="i sm" aria-hidden />
                              {t('forms.action_resend', 'Send påmindelse')}
                            </button>
                          ) : null}
                          <PatientTabMoreMenu
                            label={t('patients.tab_frame.more_actions_for', {
                              name: history.questionnaireName,
                            })}
                          >
                            <MenuItem danger onClick={() => setCancelAssignment(assignment)}>
                              {t('forms.action_cancel', 'Annuller')}
                            </MenuItem>
                          </PatientTabMoreMenu>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {completedGroups.length > 0 ? (
            <section className="sk-sec" aria-labelledby="s-done">
              <h3 id="s-done">
                {t('forms.section_completed', 'Udfyldt')}
                <span className="count num">{statusCounts.completed}</span>
              </h3>
              <div className="sk-cols sk-done" aria-hidden="true">
                <span>{t('forms.col_questionnaire', 'Spørgeskema')}</span>
                <span>{t('forms.col_completed', 'Udfyldt')}</span>
                <span>{t('forms.col_score', 'Score')}</span>
                <span>{t('forms.col_trend', 'Udvikling')}</span>
                <span />
              </div>
              <ul className="sk-l">
                {completedGroups.map((group) => {
                  const history = historyById.get(group.latest.id);
                  if (!history) return null;
                  const tags = tagsForTemplate(tagsById, group.templateId);
                  const scoreState = scoreStateFor(group.latest, history.questionnaireName);
                  const score = scoreState.display;
                  const chronological = group.all
                    .slice()
                    .reverse()
                    .map((row) => {
                      const rowState = scoreStateFor(row, history.questionnaireName);
                      if (rowState.pending) return null;
                      return scoreNumericValue(rowState.display);
                    })
                    .filter((value): value is number => value != null);
                  const spark = sparklinePoints(chronological);
                  const expanded = expandedGroups.includes(group.templateId);
                  const selected = responseAssignment?.forms_template_id === group.templateId;
                  const first = chronological[0];
                  const last = chronological[chronological.length - 1];
                  return (
                    <li key={group.templateId} className={cn('sk-it', selected && 'sel')}>
                      <div
                        className="sk-row sk-done"
                        onClick={() => setResponseAssignment(group.latest)}
                      >
                        <div className="nm">
                          <button
                            type="button"
                            className="open"
                            aria-label={t('forms.action_view_responses', {
                              defaultValue: `Se svar for ${history.questionnaireName}`,
                            })}
                            onClick={(event) => {
                              event.stopPropagation();
                              setResponseAssignment(group.latest);
                            }}
                          >
                            <b>{history.questionnaireName}</b>
                          </button>
                          <QuestionnaireTagChips
                            tags={tags}
                            emptyLabel={t('forms.no_tags', 'Ingen tags')}
                          />
                        </div>
                        <div className="c num">
                          {formatClinicDateOnly(group.latest.completed_at, i18n.language)}
                        </div>
                        <div className="sc num">
                          {scoreState.pending ? (
                            <span className="w2" aria-busy="true">
                              …
                            </span>
                          ) : null}
                          {!scoreState.pending && score.kind === 'total' ? (
                            <>
                              <b>
                                {score.value}
                                {score.max != null ? `/${score.max}` : null}
                              </b>
                              {score.severity ? (
                                <span className="w">{getSeverityLabel(score.severity, t)}</span>
                              ) : null}
                            </>
                          ) : null}
                          {!scoreState.pending && score.kind === 'physical_mental' ? (
                            <>
                              <b>{t('forms.score_physical', { value: score.physical })}</b>
                              <span className="w2">· {t('forms.score_mental', { value: score.mental })}</span>
                            </>
                          ) : null}
                          {!scoreState.pending && score.kind === 'none' ? (
                            <span className="w2">{t('forms.no_score', 'Ingen score')}</span>
                          ) : null}
                        </div>
                        <div className="tr num">
                          {group.all.length > 1 && first != null && last != null ? (
                            <>
                              <svg className="sp" width="64" height="22" viewBox="0 0 64 22" aria-hidden>
                                <polyline
                                  points={spark.points}
                                  fill="none"
                                  stroke="var(--teal)"
                                  strokeWidth="1.75"
                                  strokeLinejoin="round"
                                  strokeLinecap="round"
                                />
                                {spark.dots.map((dot) => (
                                  <circle
                                    key={`${dot.x}-${dot.y}`}
                                    cx={dot.x}
                                    cy={dot.y}
                                    r={dot.last ? 2.6 : 1.8}
                                    fill={dot.last ? 'var(--teal)' : 'var(--card)'}
                                    stroke="var(--teal)"
                                    strokeWidth="1.5"
                                  />
                                ))}
                              </svg>
                              <span>
                                <b>
                                  {first} → {last}
                                </b>
                                <span className="x">
                                  {t('forms.answers_count', {
                                    count: group.all.length,
                                    defaultValue: `${group.all.length} besvarelser`,
                                  })}
                                </span>
                              </span>
                            </>
                          ) : (
                            t('forms.answers_count', {
                              count: group.all.length,
                              defaultValue: `${group.all.length} besvarelse`,
                            })
                          )}
                        </div>
                        {group.all.length > 1 ? (
                          <button
                            type="button"
                            className="chev"
                            aria-expanded={expanded}
                            aria-label={
                              expanded
                                ? t('forms.hide_history', {
                                    count: group.all.length,
                                    name: history.questionnaireName,
                                    defaultValue: `Skjul ${group.all.length} besvarelser af ${history.questionnaireName}`,
                                  })
                                : t('forms.show_history', {
                                    count: group.all.length,
                                    name: history.questionnaireName,
                                    defaultValue: `Vis ${group.all.length} besvarelser af ${history.questionnaireName}`,
                                  })
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedGroups((current) =>
                                current.includes(group.templateId)
                                  ? current.filter((id) => id !== group.templateId)
                                  : [...current, group.templateId],
                              );
                            }}
                          >
                            {expanded ? (
                              <ChevronUp className="i sm" aria-hidden />
                            ) : (
                              <ChevronDown className="i sm" aria-hidden />
                            )}
                          </button>
                        ) : (
                          <span />
                        )}
                      </div>
                      {expanded && group.all.length > 1 ? (
                        <ul
                          className="sk-hist"
                          aria-label={t('forms.history_of', {
                            name: history.questionnaireName,
                            defaultValue: `Besvarelser af ${history.questionnaireName}`,
                          })}
                        >
                          {group.all.map((row) => {
                            const rowHistory = historyById.get(row.id);
                            const rowName = rowHistory?.questionnaireName ?? history.questionnaireName;
                            const rowState = scoreStateFor(row, rowName);
                            const rowScore = rowState.display;
                            const line = rowState.pending ? null : scoreLine(rowScore, t);
                            return (
                              <li key={row.id}>
                                <span className="d num">
                                  {formatClinicDateOnly(row.completed_at, i18n.language)}
                                </span>
                                <span>
                                  {rowState.pending ? <span aria-busy="true">…</span> : line ? <b>{line}</b> : null}
                                  {!rowState.pending && rowScore.kind === 'total' && rowScore.severity
                                    ? ` · ${getSeverityLabel(rowScore.severity, t)}`
                                    : null}
                                </span>
                                <span className="mu">
                                  {rowHistory?.appointmentStartsAt
                                    ? `${formatClinicDateTime(rowHistory.appointmentStartsAt, i18n.language)}${
                                        rowHistory.serviceName ? ` · ${rowHistory.serviceName}` : ''
                                      }`
                                    : t('forms.no_appointment', 'Uden aftale')}
                                </span>
                                <Button
                                  type="button"
                                  variant="link"
                                  size="sm"
                                  className="se"
                                  onClick={() => setResponseAssignment(row)}
                                >
                                  {t('forms.action_view_responses', 'Se svar')}
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {showClosed ? (
            <section className="sk-sec">
              <button
                type="button"
                className="sk-more"
                aria-expanded={closedExpanded}
                onClick={() => setClosedOpen((value) => !value)}
              >
                {closedExpanded ? (
                  <ChevronDown className="i sm" aria-hidden />
                ) : (
                  <ChevronRight className="i sm" aria-hidden />
                )}
                {t('forms.section_closed', 'Udløbet/annulleret')}
                <span className="count num">{closed.length}</span>
              </button>
              {closedExpanded ? (
                <>
                  <div className="sk-cols sk-closed" aria-hidden="true">
                    <span>{t('forms.col_questionnaire', 'Spørgeskema')}</span>
                    <span>{t('forms.col_sent', 'Sendt')}</span>
                    <span>{t('forms.col_status', 'Status')}</span>
                  </div>
                  <ul className="sk-l">
                    {closed.map((assignment) => {
                      const history = historyById.get(assignment.id);
                      if (!history) return null;
                      return (
                        <li key={assignment.id} className="sk-it">
                          <div className="sk-row sk-closed">
                            <div className="nm">
                              <b>{history.questionnaireName}</b>
                              <QuestionnaireTagChips
                                tags={tagsForTemplate(tagsById, assignment.forms_template_id)}
                                emptyLabel={t('forms.no_tags', 'Ingen tags')}
                              />
                            </div>
                            <div className="c num">
                              {formatClinicDateOnly(
                                assignment.expired_at ?? assignment.updated_at,
                                i18n.language,
                              )}
                            </div>
                            <div>{statusChip(assignment)}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}
            </section>
          ) : null}
        </div>
      </PatientTabFrame>

      <ConfirmActionModal
        open={Boolean(cancelAssignment)}
        title={t('forms.action_cancel', 'Annuller')}
        confirmLabel={t('forms.action_cancel', 'Annuller')}
        pending={cancelMutation.isPending}
        onConfirm={() => {
          if (!cancelAssignment) return;
          cancelMutation.mutate(
            { assignment_id: cancelAssignment.id, patient_id: patientId },
            { onSettled: () => setCancelAssignment(null) },
          );
        }}
        onClose={() => setCancelAssignment(null)}
      />

      {showSendModal && (
        <SendFormsAssignmentModal
          patientId={patientId}
          clinicId={clinicId}
          onClose={() => setShowSendModal(false)}
        />
      )}

      {responseAssignment ? (
        <FormsResponsePanel
          clinicId={clinicId}
          patientId={patientId}
          formsAssignmentId={responseAssignment.forms_assignment_id}
          templateName={historyById.get(responseAssignment.id)?.questionnaireName}
          completedAt={responseAssignment.completed_at}
          appointmentLabel={
            historyById.get(responseAssignment.id)?.appointmentStartsAt
              ? `${formatClinicDateTime(
                  historyById.get(responseAssignment.id)?.appointmentStartsAt,
                  i18n.language,
                )}${
                  historyById.get(responseAssignment.id)?.serviceName
                    ? ` · ${historyById.get(responseAssignment.id)?.serviceName}`
                    : ''
                }`
              : historyById.get(responseAssignment.id)?.serviceName
          }
          history={responseHistory(responseAssignment.forms_template_id)}
          onClose={() => setResponseAssignment(null)}
        />
      ) : null}
    </>
  );
}
